import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventParticipant } from '@frontend/sports/betting-offer/feature/model';
import { MarketGroupingConfig, PlayerStatisticsConfig } from '@frontend/sports/common/client-config-data-access';
import { injectCurrentTheme } from '@frontend/sports/common/ui/shared';
import { BaseCdsApi, BaseCdsApiFactory, CDS_API_FACTORY } from '@frontend/sports/content-distribution/feature';
import { Store } from '@ngrx/store';
import { Observable, map } from 'rxjs';

import { setPlayerStatisticsOpenAndTrackingStatus, setTeamStatisticsOpenAndTrackingStatus } from './player-statistics.actions';
import {
    MainMarketStatisticsInfo,
    PlayerStatistics,
    PlayerStatisticsContent,
    PlayerStatisticsPlayerInfo,
    PlayerStatisticsResponse,
    StatisticsInput,
    UpdateStatisticsStoreParams,
} from './player-statistics.model';

export enum SeasonStatisticsType {
    Average,
    HitRate,
}

export const isPlayerStatisticMatch = (
    { marketId, outcomeId }: PlayerStatisticsPlayerInfo,
    optionId: string | undefined,
    optionGroupId: string | undefined,
): boolean => {
    return optionId === outcomeId && marketId === optionGroupId;
};

@Injectable({ providedIn: 'root' })
export class PlayerStatisticsService {
    private currentTheme = injectCurrentTheme();
    marketGroupingConfig = inject(MarketGroupingConfig);
    apiFactory: BaseCdsApiFactory = inject(CDS_API_FACTORY);
    playerStatisticsConfig = inject(PlayerStatisticsConfig);
    cdsApi: BaseCdsApi = this.apiFactory({ endpoint: '/statistics' });
    store = inject(Store);
    readonly playerStatisticsMessages = toSignal(
        this.playerStatisticsConfig.whenReady.pipe(
            map(() => {
                const messages = this.playerStatisticsConfig.playerStatisticsInfo.messages;
                return {
                    error: messages?.error ?? '',
                    noData: messages?.noData ?? '',
                };
            }),
        ),
        { initialValue: { error: '', noData: '' } },
    );

    readonly seasonStatisticsMessage = toSignal(
        this.playerStatisticsConfig.whenReady.pipe(
            map(() => {
                const statisticsMessage = this.playerStatisticsConfig.seasonStatisticsMessages;
                return {
                    avg: statisticsMessage.Avg ?? '',
                    hitRate: statisticsMessage.HitRate ?? '',
                };
            }),
        ),
        { initialValue: { avg: '', hitRate: '' } },
    );

    fetchPlayerStats(eventId: string, mainMarket: boolean, marketFilter?: string): Observable<PlayerStatistics | undefined> {
        return this.cdsApi.get<PlayerStatisticsResponse>('/player-stats', { eventId, mainMarket, marketFilter }).pipe(
            map((data: PlayerStatisticsResponse | undefined) => {
                const content = data?.content;
                if (!content) {
                    throw new Error(undefined);
                }
                return this.reMapPlayerStatisticsContent(content);
            }),
        );
    }

    getPlayerStatisticsGraph(
        storeContent: PlayerStatistics | undefined,
        { eventId, marketId, optionId }: StatisticsInput,
        marketType?: OptionGroupType,
    ): string | undefined {
        const theme = this.currentTheme;
        if (!storeContent) {
            return undefined;
        }

        if (storeContent.mainMarketUrl) {
            return `${storeContent.mainMarketUrl}?theme=${theme}`;
        }

        const player = this.getPlayerStatistic(storeContent.playerStatistics, marketId, optionId, marketType);

        if (player?.playerNotFound) {
            return undefined;
        }

        return player && storeContent.graphPropsUrl
            ? `${storeContent.graphPropsUrl}/${eventId}/${player.marketId}/${player.outcomeId}?theme=${theme}`
            : undefined;
    }

    isPlayerStatisticsVisible(leagueId: number, optionGroup: OptionGroupType): boolean {
        const playerStatsEnabledGroups = this.marketGroupingConfig.playerStatsEnabledOptionGroup ?? {};
        return (!!leagueId && playerStatsEnabledGroups[leagueId]?.includes(optionGroup)) || false;
    }

    isPlayer(players: EventParticipant[], name: string): boolean {
        return players.some((player: EventParticipant) => player.name === name || player.shortName === name);
    }

    getPlayerStatistics(
        storeContent: PlayerStatistics | undefined,
        optionGroupId?: string,
        optionId?: string,
        isMainMarket = false,
        marketType?: OptionGroupType,
    ): PlayerStatisticsPlayerInfo | undefined {
        if (!storeContent) {
            return undefined;
        }
        if (isMainMarket) {
            return {
                isOpen: storeContent.isMainMarketOpen,
                tracking: storeContent.mainMarketTracking,
                loadedTracking: storeContent.mainMarketLoadedTracking,
            };
        }
        return this.getPlayerStatistic(storeContent.playerStatistics, optionGroupId, optionId, marketType);
    }

    openStatistics(params: UpdateStatisticsStoreParams): void {
        this.store.dispatch(setPlayerStatisticsOpenAndTrackingStatus(params));
    }

    openTeamStatistics(params: MainMarketStatisticsInfo): void {
        this.store.dispatch(setTeamStatisticsOpenAndTrackingStatus(params));
    }

    isSeasonStatisticsVisible(leagueId: number, optionGroup: OptionGroupType): boolean {
        const seasonStatsEnabledGroup = this.marketGroupingConfig.seasonStatsEnabledOptionGroup ?? {};
        return (!!leagueId && seasonStatsEnabledGroup[leagueId]?.includes(optionGroup)) || false;
    }

    private reMapPlayerStatisticsContent(content: PlayerStatisticsContent): PlayerStatistics {
        const { graph_props_url: graphPropsUrl, main_market_url: mainMarketUrl, player_stats = [] } = content;

        const playerStatistics = player_stats.map(({ player_name, full_name, market_id, outcome_id, average_stat_value, hit_rate_value }) => ({
            playerName: player_name,
            fullName: full_name,
            marketId: market_id,
            outcomeId: outcome_id,
            averageStatValue: average_stat_value,
            hitRateValue: hit_rate_value,
        }));

        return {
            graphPropsUrl,
            mainMarketUrl,
            playerStatistics,
        };
    }

    private getPlayerStatistic(
        stats: PlayerStatisticsPlayerInfo[] | undefined,
        optionGroupId: string | undefined,
        optionId: string | undefined,
        marketType?: OptionGroupType,
    ): PlayerStatisticsPlayerInfo | undefined {
        if (
            marketType &&
            [OptionGroupType.PlayerProps, OptionGroupType.PlayerMilestone, OptionGroupType.PlayerMilestoneOverUnder].includes(marketType)
        ) {
            return stats?.find((player) => player.marketId === optionGroupId);
        }

        return stats?.find((player) => isPlayerStatisticMatch(player, optionId, optionGroupId));
    }
}
